import * as React from 'react';

import Checkbox from 'af-components/Controls/Checkbox';
import Dropdown from 'af-components/Controls/Dropdown';
import Tag from 'af-components/Controls/Tag';

import styles from './styles.module.scss';

type FilterableProps<T> = {
	filterable: true;
	filterBy?: (keyof T)[];
};

type NonFilterableProps = {
	filterable?: false;
	filterBy?: never;
};

type OwnProps<T> = {
	options: T[];
	selected: Nullable<T>[];
	setSelected: (_selected: Nullable<T>[]) => void;
	idKey: keyof T;
	renderSelectedLabel?: (_selected: Nullable<T>) => React.JSX.Element;
	renderMenuItemLabel?: (item: T) => React.JSX.Element;
	labelKey?: keyof T;
	withEmptyOption?: boolean;
	lazyLoad?: (loaded: boolean) => Promise<void>;
	placeholder?: string;
} & (FilterableProps<T> | NonFilterableProps);

type Props<T> = OwnProps<T>;

function DropdownSecondaryMultiple<T extends {
	[key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
},>(props: Props<T>) {
	const {
		lazyLoad,
		options,
		placeholder,
		withEmptyOption,
		selected,
		setSelected,
		idKey,
		filterable,
		filterBy,
	} = props;

	const renderMenuItem = React.useCallback((_option: T) => {
		let label: React.JSX.Element | undefined = undefined;
		if (props.labelKey) {
			label = _option[props.labelKey] as React.JSX.Element;
		} else if (props.renderMenuItemLabel) {
			label = props.renderMenuItemLabel(_option);
		}
		return (
			<div className={styles['dropdown-field__menu-item']}>
				<span className={styles['dropdown-field__menu-item__label']}>
					{label}
				</span>
			</div>
		);
	}, [props]);

	const onValueChange = React.useCallback((_values: T[]) => {
		setSelected(_values);
	}, [setSelected]);

	const nonNullSelected = React.useMemo(() => selected?.filter((_option) => _option !== null), [selected]);

	const onRemove = React.useCallback((id: Nullable<number>) => () => {
		const newOptions = selected.filter((_selected) => (_selected?.[idKey ?? 'id'] ?? null) !== id);
		setSelected(newOptions);
	}, [idKey, selected, setSelected]);

	const tagMapper = React.useCallback((_option: Nullable<T>) => {
		const isEmptyOption = _option === null;
		const emptyClassname = isEmptyOption ? `${styles['dropdown-secondary__single-tag__option--empty']}` : '';

		return (
			<Tag contentClassName={styles['dropdown-secondary__single-tag']} key={_option?.id} >
				<span className={`${styles['dropdown-secondary__single-tag__option']} ${emptyClassname}`}>
					{!isEmptyOption
						? renderMenuItem(_option)
						: 'Empty'
					}
				</span>
				<span className="icon-close" onClick={onRemove(_option?.[idKey ?? 'id'] ?? null)} role="button" />
			</Tag>
		);
	}, [idKey, onRemove, renderMenuItem]);

	const emptyOption = React.useMemo(() => withEmptyOption ? <span>empty <Checkbox isChecked /></span> : undefined, [withEmptyOption]);

	return (
		<div className={styles['dropdown-secondary']}>
			<Dropdown<T>
				autoApplySelections={true}
				blankOption={emptyOption}
				className={styles['dropdown-secondary']}
				controlledMultiSelect={true}
				currentSelections={selected}
				defaultSelections={nonNullSelected}
				dropdownClassName={styles['dropdown-secondary__dropdown']}
				filterable={filterable}
				filterBy={filterable ? filterBy : undefined}
				hasBlankOption={true}
				isMultiselect={true}
				onLazyLoad={lazyLoad}
				onMultiselectChange={onValueChange}
				options={options}
				placeholder={placeholder}
				renderMenuItem={renderMenuItem}
				setCurrentSelections={setSelected}
				valueKey={idKey}
				withBorder={true}
				withCaret={true}
			/>
			<div className={styles['dropdown-secondary__tags']}>
				{selected?.map(tagMapper)}
			</div>
		</div>
	);
}

export default React.memo(DropdownSecondaryMultiple) as typeof DropdownSecondaryMultiple;
